import React, { useState } from 'react';
import { Cloud, Car, Home, Plane, Coins, TreeDeciduous, Sprout, Leaf, ChevronDown, ChevronUp } from 'lucide-react';
import { CarbonMetrics, formatCurrency } from '../../utils/environmentalCalculations';
import { NatureCard } from '../shared/NatureCard';
import { MetricDisplay } from '../shared/MetricDisplay';
import { BotanicalDivider } from '../shared/BotanicalDivider';
import './CarbonSection.css';

interface CarbonSectionProps {
  metrics: CarbonMetrics;
}

export const CarbonSection: React.FC<CarbonSectionProps> = ({ metrics }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="carbon-section">
      <div className="carbon-section__container">
        <div
          className="carbon-section__header"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <Cloud size={48} className="carbon-section__header-icon" />
          <div style={{ flex: 1 }}>
            <h2 className="carbon-section__title">Carbon Sequestration</h2>
            <p className="carbon-section__subtitle">
              Climate impact and carbon storage capacity
            </p>
          </div>
          {isExpanded ? <ChevronUp size={32} /> : <ChevronDown size={32} />}
        </div>

        {isExpanded && (
          <>

        <BotanicalDivider style="simple" spacing="medium" />

        {/* Context */}
        <div className="carbon-section__context">
          <div className="carbon-section__context-icon">🌍</div>
          <div className="carbon-section__context-content">
            <strong>Global Context:</strong> Carbon sequestration is one of the most valuable ecosystem
            services provided by urban trees. With a market value of ₹{(metrics.carbonValueINR / 10000000).toFixed(2)} crore,
            this represents a significant contribution to Pune's climate mitigation efforts. Urban forests
            are essential tools in achieving net-zero carbon targets.
          </div>
        </div>

        <BotanicalDivider style="leaf" spacing="medium" />

        {/* Primary Metrics */}
        <div className="carbon-section__primary">
          <NatureCard className="carbon-section__hero-card" gradient="earth">
            <div className="carbon-section__hero-value">
              {metrics.totalCO2Tonnes.toLocaleString('en-IN', { maximumFractionDigits: 1 })}
            </div>
            <div className="carbon-section__hero-label">Tonnes CO₂ Sequestered</div>
            <div className="carbon-section__hero-sublabel">
              Total carbon stored: {metrics.totalCarbonTonnes.toLocaleString('en-IN', { maximumFractionDigits: 1 })} tonnes C
            </div>
          </NatureCard>
        </div>

        {/* Equivalency Metrics */}
        <h3 className="carbon-section__section-title">Climate Impact Equivalencies</h3>
        <div className="carbon-section__grid">
          <MetricDisplay
            value={metrics.vehiclesOffset}
            label="Passenger Cars Offset"
            icon={<Car size={32} />}
            comparison="Annual emissions @ 4.6 tonnes CO₂/vehicle"
            color="earth"
            size="medium"
            delay={0.1}
          />

          <MetricDisplay
            value={metrics.householdsOffset}
            label="Households Offset"
            icon={<Home size={32} />}
            comparison="Annual emissions @ 6.5 tonnes CO₂/household"
            color="forest"
            size="medium"
            delay={0.2}
          />

          <MetricDisplay
            value={metrics.flightsOffset}
            label="Delhi-Mumbai Flights"
            icon={<Plane size={32} />}
            comparison="Round-trip per passenger"
            color="blue"
            size="medium"
            delay={0.3}
          />
        </div>

        {/* Biomass Metrics */}
        <h3 className="carbon-section__section-title">Biomass Analysis</h3>
        <p className="carbon-section__description">
          Total biomass stored in tree structures (above and below ground)
        </p>
        <div className="carbon-section__grid">
          <MetricDisplay
            value={metrics.totalBiomassTonnes.toFixed(1)}
            label="Total Biomass"
            icon={<TreeDeciduous size={32} />}
            comparison={`AGB: ${metrics.totalAGBTonnes.toFixed(1)}t | BGB: ${metrics.totalBGBTonnes.toFixed(1)}t`}
            color="forest"
            size="medium"
            delay={0.1}
          />

          <MetricDisplay
            value={metrics.totalAGBTonnes.toFixed(1)}
            label="Above Ground Biomass"
            icon={<Sprout size={32} />}
            comparison={`${(metrics.agbProportion * 100).toFixed(0)}% of total biomass`}
            color="earth"
            size="medium"
            delay={0.2}
          />

          <MetricDisplay
            value={metrics.totalBGBTonnes.toFixed(1)}
            label="Below Ground Biomass"
            icon={<Leaf size={32} />}
            comparison={`${(metrics.bgbProportion * 100).toFixed(0)}% of total biomass`}
            color="gold"
            size="medium"
            delay={0.3}
          />
        </div>

        {/* Carbon Market Valuation */}
        <NatureCard className="carbon-section__economic">
          <div className="carbon-section__economic-header">
            <Coins size={40} className="carbon-section__economic-icon" />
            <div>
              <h3 className="carbon-section__section-title">Carbon Market Valuation</h3>
              <p className="carbon-section__description">
                Based on EU ETS carbon pricing (€76.67/tonne CO₂e)
              </p>
            </div>
          </div>

          <div className="carbon-section__economic-values">
            <div className="carbon-section__economic-value">
              <div className="carbon-section__economic-label">Market Value (INR)</div>
              <div className="carbon-section__economic-amount">
                {formatCurrency(metrics.carbonValueINR)}
              </div>
            </div>
            <div className="carbon-section__economic-divider" />
            <div className="carbon-section__economic-value">
              <div className="carbon-section__economic-label">Market Value (EUR)</div>
              <div className="carbon-section__economic-amount">
                €{(metrics.carbonValueEUR / 1000).toFixed(1)}K
              </div>
            </div>
          </div>

        </NatureCard>
        </>
        )}
      </div>
    </section>
  );
};

export default CarbonSection;
